import { PackageStatus } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { PaymentService } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { BookingServices } from '@/api/services/booking';
import { paymentService } from '@/api/services/payment';
import { platformService } from '@/api/services/platform';
import { ensureStatsigUserFeatureGateReady } from '@/helpers/statsig/featureGate/ensureStatsigUserFeatureGateReady';
import { HIDE_BILLING_QUOTES } from '@/hooks/useIsBillingQuotesHidden';
import { head } from '@/router/Head';
import { resolveClusterCreationEnabled, ensureClusterIsScalable } from '@/router/resolvers/cluster';
import { resolveAccountHasPrivileges, resolveAccountHasRpcPermissions } from '@/router/resolvers/user';
import { clusterSetupSearchSchema } from '@/router/utils';
import { isClusterHybridCloud } from '@/utils/cluster-utils';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/scale')({
  validateSearch: clusterSetupSearchSchema,
  staticData: {
    title: 'Scale Cluster',
  },
  beforeLoad: async ({ context, params }) => {
    const { queryClient, clusterQuery, statsigClient } = context;
    await ensureStatsigUserFeatureGateReady(context);

    resolveClusterCreationEnabled(statsigClient, params);

    const { cluster } = await queryClient.fetchQuery(clusterQuery);

    ensureClusterIsScalable(cluster);

    const { accountId } = params;

    const bookingPackagesQueryOptions = BookingServices.listPackagesQuery({
      accountId,
      cloudProviderId: cluster.cloudProviderId,
      cloudProviderRegionId: cluster.cloudProviderRegionId,
      statuses: [PackageStatus.ACTIVE, PackageStatus.DEACTIVATED],
    });
    const listCloudProvidersQuery = platformService.listCloudProvidersQuery({ accountId });
    const listCloudProviderRegionsQuery = platformService.listCloudProviderRegionsQuery({
      accountId,
      cloudProviderId: cluster.cloudProviderId,
    });

    const canReadPaymentInformation = await resolveAccountHasRpcPermissions(
      [PaymentService.method.listPaymentMethods],
      { queryClient, accountId },
    );
    let listPaymentMethodsQuery;
    if (canReadPaymentInformation) {
      listPaymentMethodsQuery = paymentService.listPaymentMethodsQuery({ accountId });
    }

    const isBillingQuotesHidden = await resolveAccountHasPrivileges([HIDE_BILLING_QUOTES], { accountId, ...context });

    let currentQuoteOptions: ReturnType<typeof BookingServices.getQuoteQuery> | null = null;
    if (cluster.configuration?.packageId && !isBillingQuotesHidden) {
      currentQuoteOptions = BookingServices.getQuoteQuery({
        accountId,
        cloudProviderId: cluster.cloudProviderId,
        cloudProviderRegionId: cluster.cloudProviderRegionId,
        numberOfNodes: cluster.configuration?.numberOfNodes ?? 1,
        packageId: cluster.configuration.packageId,
        additionalDiskGib: cluster.configuration?.additionalResources?.disk ?? 0,
        storageTierType: cluster.configuration?.clusterStorageConfiguration?.storageTierType,
      });
      await queryClient.prefetchQuery(currentQuoteOptions);
    }

    const result = {
      listPaymentMethodsQuery,
      bookingPackagesQueryOptions,
      listCloudProvidersQuery,
      listCloudProviderRegionsQuery,
      hybridCloudEnvDetailsQueryOptions: undefined,
      currentQuoteOptions,
    };

    if (isClusterHybridCloud(cluster)) {
      return {
        ...result,
        hybridCloudEnvDetailsQueryOptions: platformService.getCloudProviderRegionQuery({
          accountId,
          regionId: cluster.cloudProviderRegionId,
          cloudProviderId: cluster.cloudProviderId,
        }),
      };
    }

    return result;
  },
  loader: ({
    context: {
      queryClient,
      listPaymentMethodsQuery,
      bookingPackagesQueryOptions,
      listCloudProvidersQuery,
      listCloudProviderRegionsQuery,
      hybridCloudEnvDetailsQueryOptions,
    },
  }) => {
    const requests: Promise<unknown>[] = [
      queryClient.fetchQuery(bookingPackagesQueryOptions),
      queryClient.fetchQuery(listCloudProvidersQuery),
      queryClient.fetchQuery(listCloudProviderRegionsQuery),
    ];
    if (listPaymentMethodsQuery) {
      requests.push(queryClient.fetchQuery(listPaymentMethodsQuery));
    }
    if (hybridCloudEnvDetailsQueryOptions) {
      requests.push(queryClient.fetchQuery(hybridCloudEnvDetailsQueryOptions));
    }
    return Promise.all(requests);
  },
  head,
  component: lazyRouteComponent(() => import('./scale.component'), 'ClusterIdScaleComponent'),
  pendingComponent: lazyRouteComponent(() => import('./scale.component'), 'ClusterIdScalePendingComponent'),
});
